import { ChangeDetectionStrategy, Component, computed, EventEmitter, input, Output } from '@angular/core';
import { filesize } from 'filesize';
import { IconName, PIconModule } from '@panaya/ui/p-icon';
import { ButtonTypeEnum, PButtonModule } from '@panaya/ui/p-button';

const options = {
    base: 2,
    standard: 'jedec',
    symbols: {
        B: 'bytes'
    }
} as const;

const FILE_ICON: IconName = 'file-text';

/**
 * One attachment as a bordered row: an icon, a label, an optional detail (a file's size or an item's type), and a
 * remove button. Pass a `File`, or describe any other attachment (a picked requirement, a reference) with `label`
 * and `icon`.
 */
@Component({
    standalone: true,
    selector: 'p-attachment-preview',
    imports: [PButtonModule, PIconModule],
    templateUrl: './p-attachment-preview.component.html',
    styleUrl: './p-attachment-preview.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PAttachmentPreviewComponent {
    protected readonly buttonType = ButtonTypeEnum;
    protected readonly shownLabel = computed(() => this.label() ?? this.file()?.name ?? '');
    protected readonly leadingDetail = computed(() => (this.file() ? '' : this.detail() ?? ''));
    protected readonly trailingDetail = computed(() => (this.file() ? this.detail() ?? formatFileSize(this.file()) : ''));

    // TODO(Angular 17.3+): replace the EventEmitter output with output()
    @Output() public removeClicked = new EventEmitter<void>();

    /** A file to show: its name becomes the label and its size the detail, unless `label` or `detail` is set. */
    public readonly file = input<File>();
    /** The text of the row; wins over the file's name. */
    public readonly label = input<string>();
    /** The icon before the label. Defaults to a document icon. */
    public readonly icon = input<IconName>(FILE_ICON);
    /**
     * Secondary text; wins over the file's size. With a file it follows the label (`report.docx 2 KB`); without
     * one it leads it, as a picked item's entity type does (`Feature 1: F1`).
     */
    public readonly detail = input<string>();
    public readonly showRemoveButton = input(true);
    /** The remove button's accessible name, already translated by the caller. */
    public readonly removeLabel = input('Remove');

    protected onRemoveClicked(): void {
        this.removeClicked.emit();
    }
}

function formatFileSize(file: File | undefined): string {
    return file?.size ? filesize(file.size, options) : '';
}
